import { useState, useCallback, useRef, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import {
  IconFileUpload,
  IconDeleteX,
  IconColorPalette,
  IconDocFolder,
  IconFiles,
  IconPen,
  IconImage,
  IconFolder,
} from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import {
  ASSET_CATEGORIES,
  isPreviewableImage,
  formatFileSize,
  inferCategoryFromFileType,
} from "../../lib/assetUtils";
import type { AssetCategory } from "../../lib/assetUtils";
import type { Owner } from "../../data/types";
import type { ReactNode } from "react";
import { OWNER_OPTIONS } from "../../lib/owner";
import { useCurrentOwner } from "../../hooks/useCurrentOwner";

const categoryIconMap: Record<AssetCategory, () => ReactNode> = {
  branding: () => <NucleoIcon icon={<IconColorPalette size={16} />} />,
  documents: () => <NucleoIcon icon={<IconDocFolder size={16} />} />,
  decks: () => <NucleoIcon icon={<IconFiles size={16} />} />,
  notes: () => <NucleoIcon icon={<IconPen size={16} />} />,
  images: () => <NucleoIcon icon={<IconImage size={16} />} />,
  other: () => <NucleoIcon icon={<IconFolder size={16} />} />,
};

interface AssetUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    title: string;
    description: string;
    category: AssetCategory;
    owner: Owner;
    file?: File;
  }) => Promise<void>;
}

export function AssetUploadDialog({ open, onOpenChange, onSubmit }: AssetUploadDialogProps) {
  const { owner: currentOwner } = useCurrentOwner();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<AssetCategory>("documents");
  const [owner, setOwner] = useState<Owner>(currentOwner);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("documents");
    setOwner(currentOwner);
    setFile(null);
    setPreview(null);
    setIsSubmitting(false);
    setIsDragOver(false);
  }, [currentOwner]);

  useEffect(() => {
    if (!open) return;
    setOwner(currentOwner);
  }, [open, currentOwner]);

  const handleFileSelect = useCallback((selected: File) => {
    setFile(selected);

    // Auto-infer category from file type
    const inferred = inferCategoryFromFileType(selected.type);
    setCategory(inferred);

    // Auto-fill title from file name if empty
    if (!title) {
      const nameWithoutExt = selected.name.replace(/\.[^.]+$/, "");
      setTitle(nameWithoutExt.replace(/[-_]/g, " "));
    }

    // Generate preview for images
    if (isPreviewableImage(selected.type)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(selected);
    } else {
      setPreview(null);
    }
  }, [title]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        category,
        owner,
        file: file ?? undefined,
      });
      resetForm();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [title, description, category, owner, file, onSubmit, onOpenChange, resetForm]);

  const removeFile = useCallback(() => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[520px] max-w-[90vw] max-h-[85vh]",
            "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-sidebar)] shadow-xl",
            "flex flex-col overflow-hidden",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            "focus-visible:outline-none",
          )}
        >
          <Dialog.Title className="text-[16px] font-semibold text-[var(--color-text-primary)] px-[var(--spacing-6)] pt-[var(--spacing-6)] pb-[var(--spacing-2)]">
            Import File
          </Dialog.Title>
          <Dialog.Description className="text-[13px] text-[var(--color-text-muted)] px-[var(--spacing-6)] pb-[var(--spacing-4)]">
            Upload branding materials, documents, decks, or any file to the team library.
          </Dialog.Description>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-[var(--spacing-6)] pb-[var(--spacing-6)] flex flex-col gap-[var(--spacing-4)]">
            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "relative rounded-[var(--radius-md)] border-2 border-dashed p-[var(--spacing-6)]",
                "flex flex-col items-center justify-center gap-[var(--spacing-2)]",
                "cursor-pointer transition-all duration-[120ms]",
                isDragOver
                  ? "border-[var(--color-accent)] bg-[var(--color-accent-900)]/20"
                  : "border-[var(--color-border-stroke)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-bg-hover)]",
              )}
            >
              {file ? (
                <div className="flex flex-col items-center gap-[var(--spacing-2)] w-full">
                  {preview ? (
                    <img src={preview} alt="Preview" className="max-h-[120px] rounded-[var(--radius-sm)] object-contain" />
                  ) : (
                    <span className="text-[var(--color-accent-300)]">
                      {categoryIconMap[category]()}
                    </span>
                  )}
                  <div className="flex items-center gap-[var(--spacing-2)]">
                    <span className="text-[13px] text-[var(--color-text-secondary)] truncate max-w-[300px]">{file.name}</span>
                    <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums">{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeFile(); }}
                      className="p-[2px] rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-state-error)] transition-colors"
                      aria-label="Remove file"
                    >
                      <NucleoIcon icon={<IconDeleteX size={14} />} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="text-[var(--color-accent-300)]">
                    <NucleoIcon icon={<IconFileUpload size={24} />} />
                  </span>
                  <p className="text-[13px] text-[var(--color-text-muted)]">
                    Drop a file here or <span className="text-[var(--color-accent)] font-medium">browse</span>
                  </p>
                  <p className="text-[11px] text-[var(--color-text-muted)]">Max 50 MB</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />
            </div>

            {/* Title */}
            <label className="flex flex-col gap-[var(--spacing-1)]">
              <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">Title *</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Brand Guidelines v2"
                required
                className={cn(
                  "h-[36px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] text-[14px] text-[var(--color-text-primary)]",
                  "placeholder:text-[var(--color-text-muted)]",
                  "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-sidebar)]",
                  "transition-colors duration-[120ms]",
                )}
              />
            </label>

            {/* Description */}
            <label className="flex flex-col gap-[var(--spacing-1)]">
              <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description…"
                rows={2}
                className={cn(
                  "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] py-[var(--spacing-2)] text-[14px] text-[var(--color-text-primary)]",
                  "placeholder:text-[var(--color-text-muted)] resize-none",
                  "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-sidebar)]",
                  "transition-colors duration-[120ms]",
                )}
              />
            </label>

            {/* Category + Owner row */}
            <div className="flex gap-[var(--spacing-3)]">
              <label className="flex flex-col gap-[var(--spacing-1)] flex-1">
                <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">Category</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as AssetCategory)}
                  className={cn(
                    "h-[36px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                    "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] text-[14px] text-[var(--color-text-primary)]",
                    "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-sidebar)]",
                    "transition-colors duration-[120ms] cursor-pointer",
                  )}
                >
                  {ASSET_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-[var(--spacing-1)] flex-1">
                <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">Owner</span>
                <select
                  value={owner}
                  onChange={(e) => setOwner(e.target.value as Owner)}
                  className={cn(
                    "h-[36px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                    "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] text-[14px] text-[var(--color-text-primary)]",
                    "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-sidebar)]",
                    "transition-colors duration-[120ms] cursor-pointer",
                  )}
                >
                  {OWNER_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-[var(--spacing-2)] pt-[var(--spacing-2)]">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className={cn(
                    "h-[36px] rounded-[var(--radius-md)] px-[var(--spacing-4)]",
                    "text-[14px] font-medium text-[var(--color-text-secondary)]",
                    "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms] cursor-pointer",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                  )}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={!title.trim() || isSubmitting}
                className={cn(
                  "h-[36px] rounded-[var(--radius-md)] px-[var(--spacing-4)]",
                  "text-[14px] font-medium text-white",
                  "bg-gradient-to-b from-[var(--color-accent-400)] to-[var(--color-accent-600)]",
                  "hover:from-[var(--color-accent-300)] hover:to-[var(--color-accent-500)]",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "transition-all duration-[120ms] cursor-pointer",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
                )}
              >
                {isSubmitting ? "Importing…" : "Import"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
